/**
 * InvoiceTable — S3 Failed-invoice list body. Columns are EXACTLY the FR-3
 * contract (design README S3 rule): identifier, deployment/customer, TPIN/
 * branch, submission timestamp, error code, error message — plus sync status
 * and created date per principle 4. No invoice payload columns (QA-1).
 *
 * Unknown/odd error codes render verbatim with an explicit "unmapped"
 * marker — never silently grouped into a triad colour (BR-2).
 */

import type { InvoiceRow } from '@domain/types'
import { NOT_RECORDED_TEXT, heldValue } from './absentValue'
import { InvoiceIdLink } from './InvoiceIdLink'

/** The known failure-signature code set is Engineering-owned (BR-2/E2) and lives in the API tier. The SPA's display contract is: render verbatim, mark unmapped. */
const KNOWN_CODE_SHAPE = /^[0-9]{1,4}$/

export function isKnownCodeShape(code: string): boolean {
  return KNOWN_CODE_SHAPE.test(code)
}

/** The one-word marker for a code the SPA cannot map (PD-37 N11); the same word on S3, S4 and S5. */
export const UNMAPPED_TEXT = 'unmapped'

/**
 * True when the source holds a result code (not null, not the empty string) that
 * is not a known shape. The same test drives the S3 code marker and the S4 status
 * qualifier (N12), so the two surfaces can never disagree about one row.
 */
export function hasUnmappedCode(code: string | null): boolean {
  return code !== null && code !== '' && !isKnownCodeShape(code)
}

/**
 * The error code of one invoice, on every surface that shows it (table, card, detail): the code verbatim,
 * with the one-word "unmapped" marker when it is not a known shape, or the absent-value copy when the
 * source holds none (null or the empty string).
 */
export function ErrorCode({ code }: { code: string | null }) {
  const held = heldValue(code)
  if (held === null) return <>{NOT_RECORDED_TEXT}</>
  if (isKnownCodeShape(held)) return <>{held}</>
  return (
    <>
      {held} <mark className="unmapped-marker">{UNMAPPED_TEXT}</mark>
    </>
  )
}

export function InvoiceTable({ rows, dbName }: { rows: readonly InvoiceRow[]; dbName: string }) {
  return (
    <table className="invoice-table" data-testid="invoice-table">
      <thead>
        <tr>
          <th scope="col">Invoice</th>
          <th scope="col">Customer</th>
          <th scope="col">TPIN / branch</th>
          <th scope="col">Submitted</th>
          <th scope="col">Error code</th>
          <th scope="col">Error message</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.rectNo} data-testid={`invoice-row-${row.rectNo}`}>
            <td>
              <InvoiceIdLink dbName={dbName} rectNo={row.rectNo} testid={`invoice-id-${row.rectNo}`}>
                {row.rectNo}
              </InvoiceIdLink>
            </td>
            <td>{dbName}</td>
            <td>
              {row.tin} / {row.bhfId}
            </td>
            <td>{heldValue(row.submissionTimestamp) ?? NOT_RECORDED_TEXT}</td>
            <td>
              <span data-testid={`invoice-code-${row.rectNo}`}>
                <ErrorCode code={row.resultCd} />
              </span>
            </td>
            <td className="error-message-cell">{heldValue(row.lastError) ?? NOT_RECORDED_TEXT}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
